// components/artifact-panel.js
// Global resizable artifact panel — 5 tabs
// Preview → Code → Highlight → Notes → History
//
// TO ADD A NEW TAB:
//   1. Build a component below
//   2. Add { id, label, component } to TABS array
//   3. Done — nothing else changes

import { useState, useRef, useEffect, useCallback } from 'react';

const MIN_WIDTH = 320;
const MAX_WIDTH_RATIO = 0.65;
const DEFAULT_WIDTH = 480;
const STORAGE_KEY = 'shift-artifact-panel-width';

function PreviewTab({ artifact }) {
  const isCode = ['code','js','jsx','ts','tsx','css','json','html','py','sql']
    .includes((artifact.type || '').toLowerCase());
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
      {isCode ? (
        <pre style={{ color: 'var(--text-2)', fontSize: '13px', lineHeight: '1.7', fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {artifact.content || '(No content)'}
        </pre>
      ) : (
        <div style={{ color: 'var(--text-2)', fontSize: '14px', lineHeight: '1.9', fontFamily: 'var(--font-serif)', whiteSpace: 'pre-wrap' }}>
          {artifact.content || '(No content)'}
        </div>
      )}
    </div>
  );
}

function CodeTab({ artifact }) {
  const [copied, setCopied] = useState(false);
  function copy() { navigator.clipboard?.writeText(artifact.content || ''); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-4)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <button onClick={copy} style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-sm)', color: copied ? 'var(--teal)' : 'var(--text-6)', fontSize: '11px', fontFamily: 'var(--font-serif)', letterSpacing: '0.06em', padding: '4px 12px', cursor: 'pointer', transition: 'all var(--transition-fast)' }}>
          {copied ? 'copied ✓' : 'copy all'}
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        <pre style={{ color: 'var(--text-3)', fontSize: '12px', lineHeight: '1.75', fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {artifact.content || '(No content)'}
        </pre>
      </div>
    </div>
  );
}

function HighlightTab({ artifact, highlights, onHighlight }) {
  const [selection, setSelection] = useState('');
  const [selPos, setSelPos] = useState(null);
  const ref = useRef(null);

  function handleMouseUp() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) { setSelection(''); setSelPos(null); return; }
    const text = sel.toString().trim();
    if (!text) return;
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const panelRect = ref.current?.getBoundingClientRect();
    if (!panelRect) return;
    setSelection(text);
    setSelPos({ top: rect.top - panelRect.top - 44, left: Math.min(rect.left - panelRect.left, panelRect.width - 180) });
  }

  function confirm() {
    if (!selection) return;
    onHighlight?.({ text: selection, createdAt: new Date().toISOString() });
    setSelection(''); setSelPos(null);
    window.getSelection()?.removeAllRanges();
  }

  function dismiss() { setSelection(''); setSelPos(null); window.getSelection()?.removeAllRanges(); }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border-4)', flexShrink: 0 }}>
        <span style={{ color: 'var(--text-7)', fontSize: '11px', fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
          Select any text — Shift reads highlights as signal
        </span>
      </div>
      <div ref={ref} style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', position: 'relative' }} onMouseUp={handleMouseUp}>
        {selection && selPos && (
          <div style={{ position: 'absolute', top: selPos.top, left: selPos.left, background: 'var(--surface-2)', border: '1px solid var(--border-1)', borderRadius: 'var(--radius-md)', padding: '6px 4px', display: 'flex', gap: '4px', zIndex: 20, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
            <button onClick={confirm} style={{ background: 'var(--teal-bg)', border: '1px solid var(--teal-border)', borderRadius: 'var(--radius-sm)', color: 'var(--teal)', fontSize: '11px', fontFamily: 'var(--font-serif)', padding: '4px 12px', cursor: 'pointer' }}>Highlight</button>
            <button onClick={dismiss} style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-sm)', color: 'var(--text-6)', fontSize: '11px', fontFamily: 'var(--font-serif)', padding: '4px 10px', cursor: 'pointer' }}>✕</button>
          </div>
        )}
        <div style={{ color: 'var(--text-2)', fontSize: '14px', lineHeight: '1.9', fontFamily: 'var(--font-serif)', whiteSpace: 'pre-wrap', userSelect: 'text' }}>
          {artifact.content || '(No content)'}
        </div>
      </div>
      {highlights && highlights.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border-4)', padding: '12px 20px', flexShrink: 0, maxHeight: '180px', overflowY: 'auto' }}>
          <div style={{ color: 'var(--text-7)', fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'var(--font-serif)', marginBottom: '8px' }}>
            {highlights.length} highlight{highlights.length !== 1 ? 's' : ''}
          </div>
          {highlights.map((h, i) => (
            <div key={i} style={{ padding: '8px 12px', marginBottom: '6px', background: 'var(--teal-surface)', borderLeft: '2px solid var(--teal)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0' }}>
              <div style={{ color: 'var(--text-3)', fontSize: '12px', lineHeight: '1.6', fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>"{h.text}"</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotesTab({ artifact, session, supabase }) {
  const [notes, setNotes] = useState([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.user?.id || !supabase) { setLoading(false); return; }
    supabase.from('notes').select('*').eq('user_id', session.user.id).eq('artifact_source', artifact.name).eq('state', 'active').order('created_at', { ascending: false })
      .then(({ data }) => { setNotes(data || []); setLoading(false); });
  }, [artifact.name, session?.user?.id]);

  async function saveNote() {
    if (!input.trim() || !session?.user?.id || !supabase) return;
    setSaving(true);
    const { data } = await supabase.from('notes').insert({ user_id: session.user.id, content: input.trim(), artifact_source: artifact.name, state: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() }).select().single();
    if (data) setNotes(prev => [data, ...prev]);
    setInput(''); setSaving(false);
  }

  async function deleteNote(id) {
    await supabase?.from('notes').delete().eq('id', id);
    setNotes(prev => prev.filter(n => n.id !== id));
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-4)', flexShrink: 0 }}>
        <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveNote(); }} placeholder="Add a note about this artifact..." rows={3}
          style={{ width: '100%', background: 'var(--surface-3)', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-md)', color: 'var(--text-1)', fontSize: '13px', fontFamily: 'var(--font-serif)', lineHeight: '1.6', padding: '10px 14px', outline: 'none', resize: 'none', boxSizing: 'border-box', transition: 'border-color var(--transition-base)' }}
          onFocus={e => e.currentTarget.style.borderColor = 'var(--teal-border)'}
          onBlur={e => e.currentTarget.style.borderColor = 'var(--border-2)'}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
          <span style={{ color: 'var(--text-8)', fontSize: '11px', fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>⌘ + Enter to save</span>
          <button onClick={saveNote} disabled={!input.trim() || saving} style={{ background: input.trim() ? 'var(--teal-bg)' : 'none', border: `1px solid ${input.trim() ? 'var(--teal-border)' : 'var(--border-3)'}`, borderRadius: 'var(--radius-sm)', color: input.trim() ? 'var(--teal)' : 'var(--text-7)', fontSize: '11px', fontFamily: 'var(--font-serif)', letterSpacing: '0.05em', padding: '5px 16px', cursor: input.trim() ? 'pointer' : 'default', transition: 'all var(--transition-fast)' }}>
            {saving ? 'saving…' : 'Save note'}
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {loading ? (
          <div style={{ color: 'var(--text-7)', fontSize: '12px', fontStyle: 'italic', textAlign: 'center', paddingTop: '24px' }}>loading…</div>
        ) : notes.length === 0 ? (
          <div style={{ color: 'var(--text-8)', fontSize: '12px', fontStyle: 'italic', textAlign: 'center', paddingTop: '24px', fontFamily: 'var(--font-serif)' }}>No notes yet. Add context, reactions, or next steps.</div>
        ) : notes.map(note => (
          <div key={note.id} style={{ padding: '12px 14px', marginBottom: '8px', background: 'var(--surface-2)', border: '1px solid var(--border-3)', borderRadius: 'var(--radius-md)', position: 'relative' }}
            onMouseEnter={e => { const b = e.currentTarget.querySelector('.nd'); if (b) b.style.opacity = '1'; }}
            onMouseLeave={e => { const b = e.currentTarget.querySelector('.nd'); if (b) b.style.opacity = '0'; }}>
            <div style={{ color: 'var(--text-3)', fontSize: '13px', lineHeight: '1.65', fontFamily: 'var(--font-serif)', whiteSpace: 'pre-wrap', paddingRight: '20px' }}>{note.content}</div>
            <div style={{ color: 'var(--text-8)', fontSize: '10px', marginTop: '6px', fontFamily: 'var(--font-serif)' }}>
              {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
            <button className="nd" onClick={() => deleteNote(note.id)} style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '2px', opacity: 0, transition: 'opacity var(--transition-fast)', lineHeight: 1, fontSize: '14px' }}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryTab({ artifact, artifactHistory, onRestoreVersion }) {
  const all = [
    ...(artifactHistory || []).map((a, i) => ({ ...a, versionNum: i + 1 })),
    { ...artifact, versionNum: (artifactHistory?.length || 0) + 1, isCurrent: true },
  ].reverse();

  if (all.length <= 1) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '10px', opacity: 0.35 }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 8v4l3 3M12 3a9 9 0 100 18A9 9 0 0012 3z" stroke="var(--text-4)" strokeWidth="1.4" strokeLinecap="round"/></svg>
        <div style={{ color: 'var(--text-6)', fontSize: '12px', fontFamily: 'var(--font-serif)' }}>No previous versions yet</div>
        <div style={{ color: 'var(--text-8)', fontSize: '11px', fontFamily: 'var(--font-serif)', fontStyle: 'italic', textAlign: 'center', maxWidth: '200px' }}>When Shift updates this artifact, previous versions appear here</div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
      {all.map((v, i) => (
        <div key={i} style={{ padding: '14px 16px', marginBottom: '8px', background: v.isCurrent ? 'var(--teal-surface)' : 'var(--surface-2)', border: `1px solid ${v.isCurrent ? 'var(--teal-border)' : 'var(--border-3)'}`, borderRadius: 'var(--radius-md)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: v.isCurrent ? 'var(--teal)' : 'var(--text-6)', fontSize: '11px', fontFamily: 'var(--font-serif)', fontWeight: 'bold', letterSpacing: '0.05em' }}>v{v.versionNum}</span>
              {v.isCurrent && <span style={{ background: 'var(--teal-bg)', border: '1px solid var(--teal-border)', borderRadius: 'var(--radius-pill)', color: 'var(--teal)', fontSize: '9px', letterSpacing: '0.08em', padding: '1px 7px', fontFamily: 'var(--font-serif)', textTransform: 'uppercase' }}>current</span>}
            </div>
            {!v.isCurrent && (
              <button onClick={() => onRestoreVersion?.(v)} style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-sm)', color: 'var(--text-6)', fontSize: '10px', fontFamily: 'var(--font-serif)', letterSpacing: '0.05em', padding: '3px 10px', cursor: 'pointer', transition: 'all var(--transition-fast)' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-1)'; e.currentTarget.style.borderColor = 'var(--border-1)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-6)'; e.currentTarget.style.borderColor = 'var(--border-2)'; }}>
                restore
              </button>
            )}
          </div>
          <div style={{ color: v.isCurrent ? 'var(--text-3)' : 'var(--text-5)', fontSize: '12px', lineHeight: '1.6', fontFamily: 'var(--font-serif)', whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {v.content || '(No content)'}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── TAB REGISTRY — add new tabs here only ───────────
const TABS = [
  { id: 'preview',   label: 'Preview',   component: PreviewTab },
  { id: 'code',      label: 'Code',      component: CodeTab },
  { id: 'notes',     label: 'Notes',     component: NotesTab },
  // { id: 'highlight', label: 'Highlight', component: HighlightTab },  ← build after Pin & Highlight system
  // { id: 'history',   label: 'History',   component: HistoryTab },    ← build after artifact versioning
];

export default function ArtifactPanel({ artifact, artifactHistory = [], onClose, onSendToThread, onSaveToPlaybook, onRestoreVersion, session, supabase, isMobile }) {
  const [activeTab, setActiveTab] = useState('preview');
  const [highlights, setHighlights] = useState([]);
  const [width, setWidth] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_WIDTH;
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved) : DEFAULT_WIDTH;
  });
  const [dragging, setDragging] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [savedToPlaybook, setSavedToPlaybook] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  useEffect(() => { setActiveTab('preview'); setHighlights([]); setShareOpen(false); }, [artifact?.name]);

  function downloadArtifact() {
    if (!artifact?.content) return;
    const type = (artifact.type || '').toLowerCase();
    let mimeType = 'text/plain';
    let ext = '.txt';
    if (type === 'html' || type === 'web') { mimeType = 'text/html'; ext = '.html'; }
    else if (type === 'code' || type === 'javascript' || type === 'js' || type === 'jsx') { ext = '.js'; }
    else if (type === 'python' || type === 'py') { ext = '.py'; }
    else if (type === 'css') { ext = '.css'; }
    else if (type === 'json') { ext = '.json'; }
    else if (type === 'sql') { ext = '.sql'; }
    else if (type === 'ts' || type === 'tsx' || type === 'typescript') { ext = '.ts'; }
    const blob = new Blob([artifact.content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (artifact.name || 'artifact') + ext;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const onDragStart = useCallback((e) => {
    dragStartX.current = e.clientX;
    dragStartWidth.current = width;
    setDragging(true);
    e.preventDefault();
  }, [width]);

  useEffect(() => {
    if (!dragging) return;
    const maxW = window.innerWidth * MAX_WIDTH_RATIO;
    function onMove(e) {
      const delta = dragStartX.current - e.clientX;
      setWidth(Math.min(maxW, Math.max(MIN_WIDTH, dragStartWidth.current + delta)));
    }
    function onUp() {
      setDragging(false);
      localStorage.setItem(STORAGE_KEY, String(Math.round(width)));
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, width]);

  const ActiveTab = TABS.find(t => t.id === activeTab)?.component || PreviewTab;

  return (
    <>
      {!isMobile && (
        <div onMouseDown={onDragStart} style={{ width: '4px', flexShrink: 0, background: dragging ? 'var(--teal-border)' : 'transparent', borderLeft: '1px solid var(--border-4)', cursor: 'col-resize', zIndex: 10, transition: 'background var(--transition-fast)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--border-2)'}
          onMouseLeave={e => { if (!dragging) e.currentTarget.style.background = 'transparent'; }}
        />
      )}
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: isMobile ? '100%' : `${width}px`, background: 'var(--surface-1)', borderLeft: '1px solid var(--border-4)', display: 'flex', flexDirection: 'column', overflow: 'hidden', userSelect: dragging ? 'none' : 'auto', zIndex: 150 }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: '12px', background: 'var(--surface-1)' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: 'var(--text-1)', fontSize: '14px', fontFamily: 'var(--font-serif)', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{artifact.name}</div>
            <div style={{ color: 'var(--text-7)', fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'var(--font-serif)', marginTop: '2px' }}>
              {artifact.type || 'artifact'}{artifactHistory.length > 0 && <span style={{ color: 'var(--text-8)', marginLeft: '8px' }}>v{artifactHistory.length + 1}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {onSaveToPlaybook && (
              <button onClick={() => { onSaveToPlaybook?.(artifact); setSavedToPlaybook(true); setTimeout(() => setSavedToPlaybook(false), 2000); }} style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-sm)', color: savedToPlaybook ? 'var(--teal)' : 'var(--text-6)', fontSize: '11px', fontFamily: 'var(--font-serif)', letterSpacing: '0.05em', padding: '5px 12px', cursor: 'pointer', transition: 'all var(--transition-fast)', whiteSpace: 'nowrap' }}>
                {savedToPlaybook ? '★ saved' : '+ Playbook'}
              </button>
            )}
            {onSendToThread && (
              <button onClick={() => onSendToThread(artifact.content)} style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-sm)', color: 'var(--text-6)', fontSize: '11px', fontFamily: 'var(--font-serif)', letterSpacing: '0.05em', padding: '5px 12px', cursor: 'pointer', transition: 'all var(--transition-fast)', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.borderColor = 'var(--border-1)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-6)'; e.currentTarget.style.borderColor = 'var(--border-2)'; }}>
                → thread
              </button>
            )}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShareOpen(o => !o)} style={{ background: shareOpen ? 'var(--teal-bg)' : 'none', border: `1px solid ${shareOpen ? 'var(--teal-border)' : 'var(--border-2)'}`, borderRadius: 'var(--radius-sm)', color: shareOpen ? 'var(--teal)' : 'var(--text-6)', fontSize: '11px', fontFamily: 'var(--font-serif)', letterSpacing: '0.05em', padding: '5px 12px', cursor: 'pointer', transition: 'all var(--transition-fast)' }}>Share ▾</button>
              {shareOpen && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', background: 'var(--surface-2)', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-md)', overflow: 'hidden', zIndex: 50, minWidth: '160px', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>
                  {[
                    { label: 'Copy text', action: () => navigator.clipboard?.writeText(artifact.content || '') },
                    { label: 'Download .md', action: () => artifact.downloadUrl && window.open(artifact.downloadUrl) },
                    { label: 'Export as PDF', action: () => {} },
                    { label: 'Send to email', action: () => {} },
                  ].map(({ label, action }, i, arr) => (
                    <button key={label} onClick={() => { action(); setShareOpen(false); }} style={{ display: 'block', width: '100%', background: 'none', border: 'none', borderBottom: i < arr.length - 1 ? '1px solid var(--border-4)' : 'none', color: 'var(--text-4)', fontSize: '12px', fontFamily: 'var(--font-serif)', letterSpacing: '0.04em', padding: '10px 16px', cursor: 'pointer', textAlign: 'left', transition: 'all var(--transition-fast)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-4)'; e.currentTarget.style.color = 'var(--text-1)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-4)'; }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={downloadArtifact} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, opacity: 0.6, lineHeight: 1, transition: 'opacity var(--transition-fast)' }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}>
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path d="M9 4v8" stroke="var(--text-4)" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M5.5 9L9 12.5L12.5 9" stroke="var(--text-4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 14.5h12" stroke="var(--text-4)" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-6)', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 2px', transition: 'color var(--transition-fast)' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--text-1)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-6)'}>×</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-4)', flexShrink: 0, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === tab.id ? 'var(--teal)' : 'transparent'}`, color: activeTab === tab.id ? 'var(--teal)' : 'var(--text-6)', fontSize: '13px', fontFamily: 'var(--font-serif)', letterSpacing: '0.04em', padding: '10px 18px', cursor: 'pointer', flexShrink: 0, transition: 'all var(--transition-base)' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Active tab content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ActiveTab artifact={artifact} artifactHistory={artifactHistory} highlights={highlights} onHighlight={h => setHighlights(prev => [h, ...prev])} onRestoreVersion={onRestoreVersion} session={session} supabase={supabase} />
        </div>
      </div>
    </>
  );
}
